'use client';
import { useStore } from '@/providers/app-store-provider';
import { getSetLocalStorage } from '@/utils/tools';
import { useEffect, useMemo } from 'react';

/**
 * 检查是否满足 irclickid 条件的 Hook
 * 对应 Vue 组件中的 isIrclickid 计算属性逻辑
 */
export const useIrclickidCheck = () => {
  const userInfo = useStore((state) => state.userInfo);
  const token = useStore((state) => state.token);

  // 检查并缓存 source=impact 标识
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (window.location.href.includes('source=impact')) {
        // 缓存30天 (30 * 24 * 60 * 60 * 1000 毫秒)
        const expireTime = Date.now() + 30 * 24 * 60 * 60 * 1000;
        getSetLocalStorage.set('source_impact_flag', true, expireTime);
      }
    }
  }, []);

  // 检查是否满足 irclickid 条件
  const isIrclickid = useMemo(() => {
    if (typeof window === 'undefined') return false;

    // 检查URL中是否包含irclickid或source=impact
    const hasIrclickidInUrl = window.location.href.includes('irclickid');
    const hasSourceImpactInUrl = window.location.href.includes('source=impact');

    // 检查缓存中是否有source=impact标识
    const hasSourceImpactInCache = getSetLocalStorage.get('source_impact_flag');

    return hasIrclickidInUrl || hasSourceImpactInUrl || hasSourceImpactInCache;
  }, []);

  // 获取 irclickid 值的方法
  const getIrclickid = useMemo(() => {
    return () => {
      // 检查是否有token（store中的或localStorage中的）
      const storeToken = token;
      const localToken = typeof window !== 'undefined' ? localStorage.getItem('zl-token') : null;
      const hasToken = storeToken || localToken;

      if (hasToken && userInfo?.irclickid) {
        // 已登录用户从用户信息中获取
        return userInfo.irclickid;
      } else if (typeof window !== 'undefined') {
        // 游客从缓存中获取
        return getSetLocalStorage.get('irclickid');
      }
      return null;
    };
  }, [token, userInfo?.irclickid]);

  // 获取包含 irclickid 的 web_url
  const getWebUrlWithIrclickid = useMemo(() => {
    return (currentUrl: string) => {
      // 检查当前URL是否已包含irclickid
      if (currentUrl.includes('irclickid')) {
        return currentUrl;
      }

      // 尝试获取irclickid
      const irclickid = getIrclickid();
      if (!irclickid) {
        return currentUrl;
      }

      // 添加irclickid到URL
      const separator = currentUrl.includes('?') ? '&' : '?';
      return `${currentUrl}${separator}irclickid=${irclickid}`;
    };
  }, [getIrclickid]);

  return {
    isIrclickid,
    getIrclickid,
    getWebUrlWithIrclickid,
  };
};
